import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@shared/api/client'
import { endpoints } from '@shared/api/endpoints'
import { QUERY_CACHE_KEYS } from '@shared/api/queryKeys'
import type { Paginated } from '@shared/lib'
import type { LocationCity, LocationCountry } from '../model/types'

const ALL_PAGE_SIZE = 1000
const STALE_MS = 5 * 60 * 1000
/** Страховка от бесконечного цикла, если бэк перестанет отдавать `count`.
 *  Каталог — 847 городов на странице в 100, то есть 9 запросов. */
const MAX_CITY_PAGES = 30

/** Каталог городов целиком.
 *
 *  Идём по страницам, а не просим одну большую: `page_size` бэк ограничивает
 *  сотней (проверено на стенде 2026-08-06 — запрос на 1000 вернул 100 из 847
 *  и `next`). Без обхода страниц каталог обрывался на алфавитно-ранних
 *  странах, и город слота молча не находился по id. По `next` не идём: он
 *  приходит абсолютной `http`-ссылкой в обход клиента с авторизацией. */
async function fetchAllCities(country?: string): Promise<LocationCity[]> {
  const fetchPage = (page: number) =>
    apiClient.get<Paginated<LocationCity>>(endpoints.locations.cities, {
      params: { page, page_size: ALL_PAGE_SIZE, ...(country ? { country } : {}) },
    })
  // Первая страница говорит, сколько их всего, остальные берём параллельно:
  // девять последовательных запросов у админа держали каталог несколько секунд.
  const first = await fetchPage(1)
  const all: LocationCity[] = [...first.data.results]
  const pageSize = first.data.results.length
  if (pageSize > 0 && all.length < first.data.count) {
    const lastPage = Math.min(Math.ceil(first.data.count / pageSize), MAX_CITY_PAGES)
    const rest = await Promise.all(Array.from({ length: lastPage - 1 }, (_, i) => fetchPage(i + 2)))
    for (const r of rest) all.push(...r.data.results)
  }
  // Бэк может не фильтровать по `country` — подстраховываемся на клиенте.
  return country ? all.filter((c) => c.country === country) : all
}

/** Общие настройки запроса каталога городов. Ключ различается по стране,
 *  поэтому «все города» и «города страны» не конфликтуют в кеше. */
function citiesQuery(country?: string) {
  return {
    queryKey: QUERY_CACHE_KEYS.locations.cities(country),
    queryFn: () => fetchAllCities(country),
    staleTime: STALE_MS,
  }
}

/**
 * Каталог городов, ключом по первичному ключу.
 *
 * Слот проведения олимпиады хранит город числом, а показать надо название.
 * Фильтровать по стране здесь нечем: в одной олимпиаде площадки бывают из
 * разных стран. Отдельного запроса это не стоит — {@link useCities} и так
 * тянет каталог целиком и отсеивает лишнее на клиенте.
 *
 * `enabled` обязателен вызывающему: каталог — 847 городов, то есть девять
 * запросов. У онлайн-олимпиады городов нет вовсе, и превью гоняло их вхолостую
 * на каждом открытии шага 5.
 */
export function useCityNameById(enabled = true) {
  const { data } = useQuery<LocationCity[]>({ ...citiesQuery(), enabled })

  return useMemo(() => new Map((data ?? []).map((c) => [String(c.id), c.name])), [data])
}

export function useCountries() {
  return useQuery<LocationCountry[]>({
    queryKey: QUERY_CACHE_KEYS.locations.countries,
    queryFn: async () => {
      const r = await apiClient.get<Paginated<LocationCountry>>(endpoints.locations.countries, {
        params: { page_size: ALL_PAGE_SIZE },
      })
      return r.data.results
    },
    staleTime: STALE_MS,
  })
}

export function useCities(country?: string) {
  return useQuery<LocationCity[]>({ ...citiesQuery(country), enabled: !!country })
}
